import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useMessages, useSendMessage } from '@/hooks/useDMs';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { formatDate } from '@/lib/format';

export function DMThreadPage() {
  const { id } = useParams<{ id: string }>();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useMessages(id || '');
  const sendMessage = useSendMessage(id || '');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage.mutate(input.trim());
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Messages come newest first from API, reverse for display
  const sortedMessages = [...(messages || [])].reverse();

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-border/50 glass">
        <Link to="/dms" className="min-touch flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition-colors">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        <h1 className="font-display font-bold text-base">Conversation</h1>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <LoadingSpinner />
        ) : sortedMessages.length > 0 ? (
          sortedMessages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender_id === id ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                  msg.sender_id === id
                    ? 'bg-surface text-zinc-200 rounded-bl-md'
                    : 'bg-neon/10 text-zinc-200 rounded-br-md'
                }`}
              >
                <p className="text-sm font-body whitespace-pre-wrap">{msg.content}</p>
                <p className="text-[10px] text-dim mt-1">{formatDate(msg.created_at)}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted text-sm font-body">No messages yet. Say hello!</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border/50 glass">
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-4 py-3 bg-surface rounded-xl border border-border focus:border-neon/50 focus:shadow-neon-sm focus:outline-none transition-all duration-200 font-body text-sm text-zinc-200 placeholder:text-dim"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sendMessage.isPending}
            className="min-touch flex items-center justify-center w-11 h-11 rounded-xl bg-neon text-void hover:bg-neon-dim transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
